// Decodes the obfuscated A/B cohort tag carried on email links as the `abc` query param.
//
// Mirrors the backend encoder (SecUtils.encode_obfuscated_string): a plain XOR against a fixed,
// non-secret key, then base62 (pure alphanumeric, so nothing in the email/URL pipeline can
// mangle it). Obfuscation, NOT security — it only keeps a recipient from casually reading or
// editing their assignment. The key, alphabet, and scheme MUST byte-match the Ruby side, and
// intentionally share nothing with the opaque-URL encoding.
//
// The decoded value is the self-describing "<test>:<cohort>" tag, which we split into two
// Amplitude attributes (ab_test, ab_cohort).

export const AB_COHORT_PARAM = "abc"

// Must byte-match SecUtils::OBFUSCATED_STRING_KEY. ASCII only, so charCodeAt == byte.
const AB_COHORT_KEY = "mt-ab-cohort-2026"

// Matches the Ruby `base62` gem alphabet (digits, upper, lower) used by
// SecUtils.encode_obfuscated_string. The cross-language vector test enforces parity.
const BASE62_ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"

function base62ToBytes(token: string): Uint8Array | null {
  if (!/^[0-9A-Za-z]+$/.test(token)) return null

  let num = 0n
  for (const ch of token) {
    num = num * 62n + BigInt(BASE62_ALPHABET.indexOf(ch))
  }

  // Mirror of the Ruby decode: strip bytes until the leading-1 sentinel remains.
  const bytes: number[] = []
  while (num > 1n) {
    bytes.unshift(Number(num & 0xffn))
    num >>= 8n
  }
  return Uint8Array.from(bytes)
}

// Returns the decoded "<test>:<cohort>" tag, or null for a malformed/tampered token.
export function decodeAbCohort(token: string): string | null {
  const scrambled = base62ToBytes(token)
  if (!scrambled) return null

  try {
    const out = new Uint8Array(scrambled.length)
    for (let i = 0; i < scrambled.length; i++) {
      out[i] = scrambled[i] ^ AB_COHORT_KEY.charCodeAt(i % AB_COHORT_KEY.length)
    }
    return new TextDecoder("utf-8", { fatal: true }).decode(out)
  } catch {
    return null
  }
}

export type AbCohort = { ab_test: string; ab_cohort: string }

// Decodes and splits the tag into its two parts. Null if the token is malformed or not a
// "<test>:<cohort>" shape.
export function parseAbCohort(token: string): AbCohort | null {
  const decoded = decodeAbCohort(token)
  if (!decoded) return null

  const sep = decoded.indexOf(":")
  if (sep < 0) return null

  return { ab_test: decoded.slice(0, sep), ab_cohort: decoded.slice(sep + 1) }
}

export type StoredCohort = AbCohort & { sessionId?: number }

// Decides what the enrichment plugin should do for one event, given the stored cohort and the
// current Amplitude sessionId. Kept pure (no SDK/storage access) so the session-binding logic
// is unit-testable.
//
// The cohort is captured before amplitude.init() finishes setting the sessionId, so it starts
// unbound. We bind it to the first real sessionId we see — we do NOT drop an unbound cohort
// just because getSessionId() isn't ready yet (that was the original race bug). Once bound, a
// changed sessionId means a new Amplitude session, so we stop labeling.
export function resolveCohortForSession(
  stored: StoredCohort | null,
  sessionId: number | undefined
): { label: AbCohort | null; store: StoredCohort | null; drop: boolean } {
  if (!stored) return { label: null, store: null, drop: false }

  if (stored.sessionId !== undefined && stored.sessionId !== sessionId) {
    return { label: null, store: null, drop: true }
  }

  const bindNow = stored.sessionId === undefined && sessionId !== undefined
  return {
    label: { ab_test: stored.ab_test, ab_cohort: stored.ab_cohort },
    store: bindNow ? { ...stored, sessionId } : null,
    drop: false,
  }
}
